import { getAddressDisplayString } from '@components/AddressDisplay';
import {
  AutoComplete,
  Props as AutoCompleteProps,
  Shell,
} from '@components/AutoComplete';
import { getDatetimeValue } from '@components/DatetimeValue';
import { Icon } from '@components/Icon';
import { PRIVATE_themeObj } from '@components/Theme';
import { useGetConnectCarrierSearchLazyQuery } from '@connect-carrier/generated/queries/getConnectCarrierSearch';
import { CarrierSearchResult } from '@connect-carrier/generated/types';
import { getSelectedCustomer } from '@connect-carrier/harness/customer';
import { Roles, useHasRole } from '@connect-carrier/hooks/useHasRole';
import { useTenantTheme } from '@connect-carrier/hooks/useTenantTheme';
import { useDebouncedFn } from '@hooks/useDebouncedFn';
import { useTheme } from '@hooks/useTheme';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { pipeSeparator } from '@utils/htmlEntities';
import { compact, isEmpty } from 'lodash-es';
import { ReactNode, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWindowSize } from 'react-use';

interface GlobalSearchProps {
  onClose?: () => void;
}

export const GlobalSearch = ({ onClose }: GlobalSearchProps): JSX.Element => {
  const { colors, gray } = useTheme();
  const { width } = useWindowSize();
  const desktopResp = width >= 1301;
  const tenantConfig = useTenantTheme();

  const containerRef = useRef<HTMLDivElement>(null);

  const selectedCustomer = getSelectedCustomer();

  const navigate = useNavigate();
  const isPricing = useHasRole(Roles.Pricing);
  const [getSearchResultQuery, { loading, data }] =
    useGetConnectCarrierSearchLazyQuery();

  const debouncedSearchResult = useDebouncedFn(getSearchResultQuery, 500, []);

  const [visibleResultsCount, setVisibleResultsCount] = useState(10);

  const searchResults = useMemo(() => {
    return (
      (data?.getConnectCarrierSearch?.results as CarrierSearchResult[]) ?? []
    );
  }, [data]);

  const formattedListRow: (result: CarrierSearchResult) => JSX.Element = (
    result,
  ) => {
    const originAddress = getAddressDisplayString({
      value: {
        city: result.originStopCity || '',
        state: result.originStopState || '',
      },
      city: true,
      state: true,
    });
    const destinationAddress = getAddressDisplayString({
      value: {
        city: result.destStopCity || '',
        state: result.destStopState || '',
      },
      city: true,
      state: true,
    });
    let pickupDate = '';
    if (result.availablePuDate) {
      pickupDate = getDatetimeValue({
        value: result.availablePuDate,
        timezone: 'UTC',
        dateOnly: true,
      });
    }
    const addressDisplay = compact([originAddress, destinationAddress]).join(
      ' → ',
    );
    const restLabel = compact([pickupDate, addressDisplay]).join(' | ');
    const prefix = t`Route #${result.routeCode ?? ''}`;

    return (
      <div css={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Icon i="boxOpen" size="sm" color="inherit" css={{ flexShrink: 0 }} />
        <div>
          <strong>{prefix}</strong>
          {pipeSeparator}
          {restLabel}
        </div>
      </div>
    );
  };

  const displayItems = useMemo(() => {
    const visibleResults = searchResults?.slice(0, visibleResultsCount);
    const hasMoreResults = searchResults.length > visibleResultsCount;

    const items: Array<Shell<CarrierSearchResult>> =
      visibleResults?.map((result) => {
        return {
          id: result?.routeId?.toString(),
          label: (): ReactNode => {
            return formattedListRow(result);
          },
          value: result,
        };
      }) ?? [];

    if (hasMoreResults) {
      items.push({
        // Disabled so Downshift doesn't treat this as a selectable item. Selecting it would
        // otherwise reset the AutoComplete's input value (via itemToString) and re-trigger
        // onInputValueChange, wiping out the expanded results we just loaded.
        disabled: true,
        value: undefined,
        label: (): JSX.Element => (
          <a
            href=""
            css={{
              display: 'block',
              textAlign: 'center',
              color: `${PRIVATE_themeObj.colors.primary} !important`,
            }}
            data-testid="expand-your-search"
            onClick={(event): void => {
              event.preventDefault();
              event.stopPropagation();
              setVisibleResultsCount(searchResults.length);
            }}
          >
            <Trans>Expand your search</Trans>
            <Icon
              i="arrowRightSolid"
              size="sm"
              color="primary"
              css={{ marginLeft: '6px' }}
            />
          </a>
        ),
        id: 'load-more',
      });
    }

    if (
      isEmpty(items) &&
      !loading &&
      data &&
      isEmpty(data?.getConnectCarrierSearch?.results)
    ) {
      items.push({
        id: 'no-results',
        label: (): JSX.Element => (
          <div
            css={{
              display: 'block',
              textAlign: 'center',
              color: `${PRIVATE_themeObj.colors.primary} !important`,
            }}
            data-testid="no-search-results"
          >
            <Trans>Didn&apos;t find what you&apos;re looking for?</Trans>
          </div>
        ),
        value: undefined,
        disabled: true,
      });
    }

    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults, visibleResultsCount, loading, data]);

  const handleOnChange: AutoCompleteProps<CarrierSearchResult>['onChange'] = (
    selectedItem,
    helpers,
  ): void => {
    if (!selectedItem?.value?.routeId) {
      return;
    }

    helpers?.clearSelection();
    helpers?.closeMenu();
    // Blur the input field to remove focus and close the virtual keyboard on mobile devices
    containerRef.current?.querySelector('input')?.blur();
    switch (selectedItem.value.targetTab) {
      case 'myRoutes':
        navigate(`/route-details/${selectedItem?.value.routeId}`);
        break;
      case 'availableRoutes':
        navigate(
          `/available-route-details/${`${selectedItem?.value.routeId}${selectedItem?.value.offerId ? `__${(selectedItem?.value.offerId as string).toLowerCase()}` : ''}`}`,
        );
        break;
      case 'offers':
        navigate(
          `/offer-details/${`${selectedItem?.value.routeId}${selectedItem?.value.offerId ? `__${(selectedItem?.value.offerId as string).toLowerCase()}` : ''}`}`,
        );
        break;
      case 'tenders':
        navigate(
          `/tender-details/${selectedItem?.value.routeId}__${selectedItem?.value.tenderId?.toLowerCase()}__${selectedItem?.value.tenderPlanId?.toLowerCase()}`,
        );
        break;
      case 'accounting':
        if (isPricing) {
          navigate(`/accounting-route-details/${selectedItem?.value.routeId}`);
        } else {
          navigate(`/route-details/${selectedItem?.value.routeId}`, {
            state: { fromAccounting: true },
          });
        }
        break;
      default:
        break;
    }
    // Close the sidebar drawer on mobile/tablet after navigating to a search result
    if (!desktopResp) {
      onClose?.();
    }
  };

  return (
    <div
      ref={containerRef}
      data-testid="search-navigation"
      css={{
        width: desktopResp ? '600px' : 'inherit',
        height: '40px',
        margin: '10px',
      }}
    >
      <AutoComplete
        onInputValueChange={(inputValue): void => {
          setVisibleResultsCount(10); // Reset visible count on new search
          debouncedSearchResult({
            variables: {
              input: {
                carrierId: selectedCustomer?.mmApprovedRecordIds as string[],
                searchTerm: inputValue,
                first: 100,
              },
            },
          });
        }}
        itemToString={(item) =>
          item?.value && item?.value?.routeCode
            ? t`Route #${item?.value?.routeCode ?? ''}`
            : ''
        }
        onChange={handleOnChange}
        items={displayItems}
        showSearchIcon
        loading={loading}
        css={{
          '[data-icon="magnifying-glass"]': {
            top: '13px',
            left: '15px',
          },
          'ul[role="listbox"]': {
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
          },
        }}
        inputProps={{
          placeholder: t`Search routes`,
          'data-testid': 'global-search-input',
          css: {
            paddingTop: 8,
            paddingBottom: 8,
            paddingLeft: 40,
            paddingRight: 16,
            borderRadius: 5,
            backgroundColor: desktopResp
              ? (tenantConfig?.theme?.searchBg ?? gray[90])
              : 'inherit',
            border: desktopResp ? 'none' : `1px solid ${gray[80]}`,
            height: '40px',
            fontSize: 14,
            '&::placeholder': {
              color: colors.disabledText,
            },
          },
        }}
      />
    </div>
  );
};
